import { useReaction, useReactionNow } from '@speedlo/hooks'
import { debounce } from 'debounce'
import { useLocalStore } from 'mobx-react-lite'
import React from 'react'

import { useRoot } from '../../../app/useRoot'
import { QResolveLocationResolveLocation } from '../../../graph/generated'
import { formatAddress } from '../../../helpers/formats'
import { useLocationResolverQuery } from '../../../hooks/useLocationResolverQuery'

export type AddressSearchResult = ReturnType<typeof mapResult>

export function useAddressSearch() {
  const { location } = useRoot()
  const resolver = useLocationResolverQuery()

  const state = useLocalStore(() => ({
    results: null as RoA<AddressSearchResult> | null,
    phrase: location.searchValue,
    searching: false,
  }))

  const doFetch = React.useCallback(async () => {
    if (state.phrase.length === 0) {
      state.results = null
      return
    }

    location.log(`searching phrase: ${state.phrase}`)
    state.searching = true

    const response = await resolver(null, state.phrase)
    const validResults = [mapResult(response.resolveLocation)]

    state.results = validResults
    state.searching = false
  }, [location, resolver, state.phrase, state.results, state.searching])

  const debouncedFetch = React.useRef(debounce(doFetch, 500))

  const onSearch = React.useCallback(
    (nextPhrase: string = location.searchValue) => {
      location.log(`onSearch ${nextPhrase}`)
      state.phrase = nextPhrase.trim()
      debouncedFetch.current.clear()
      doFetch()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doFetch, location],
  )

  useReaction(
    () => location.searchValue,
    value => {
      if (value.length === 0) {
        state.phrase = ''
        state.results = null
        debouncedFetch.current.clear()
      }
    },
  )

  useReactionNow(
    () => location.initialSearchAddress,
    value => {
      if (value) {
        onSearch(value)
      }
    },
  )

  useReactionNow(
    () => location.searchValue,
    (value, r) => {
      if (value) {
        onSearch(value)
      }
      r.dispose()
    },
  )

  return { ...state, onSearch }
}

function mapResult(userData: QResolveLocationResolveLocation) {
  return {
    gps: userData.gpsCoordinates ?? null,
    address: userData.finalAddress ? formatAddress(userData.finalAddress) : ``,
  }
}
